import { Coin } from '@repo/studio-design-system-ui-coin'

import * as React from 'react'
import { AnimatePresence, m } from 'framer-motion'
import { Portal } from 'primitives/portal'
import { tw } from 'utils/classnames'

type RegisterFn = (node: HTMLElement | null) => void

interface TriggerOptions {
  count?: number
  source?: HTMLElement | null
}

interface CoinTransferAnimationContextValue {
  registerSource: RegisterFn
  registerTarget: RegisterFn
  trigger: (options?: TriggerOptions) => void
}

interface Point {
  x: number
  y: number
}

interface CoinKeyframe {
  id: number
  x: number[]
  y: number[]
  delay: number
  duration: number
  rotation: number
}

const CoinTransferAnimationContext = React.createContext<
  CoinTransferAnimationContextValue | undefined
>(undefined)

function createCoinKeyframes(params: {
  id: number
  order: number
  start: Point
  end: Point
}): CoinKeyframe {
  const { id, order, start, end } = params
  const duration = 1
  const delay = Math.random() * 0.03 + order * 0.025
  const rotation = (Math.random() - 0.5) * 60
  const angle = Math.random() * Math.PI * 2
  const burstDistance = 32 + Math.random() * 32
  const burstPoint = {
    x: start.x + Math.cos(angle) * burstDistance,
    y: start.y + Math.sin(angle) * burstDistance,
  }
  return {
    id,
    x: [start.x, burstPoint.x, end.x],
    y: [start.y, burstPoint.y, end.y],
    delay,
    duration,
    rotation,
  }
}

export function CoinTransferAnimationProvider({ children }: { children: React.ReactNode }) {
  const sourceRef = React.useRef<HTMLElement | null>(null)
  const targetRef = React.useRef<HTMLElement | null>(null)
  const animationId = React.useRef(0)
  const [coins, setCoins] = React.useState<CoinKeyframe[]>([])

  const registerSource = React.useCallback<RegisterFn>(node => {
    if (node && node !== sourceRef.current) {
      sourceRef.current = node
      return
    }
    if (!node && sourceRef.current) {
      sourceRef.current = null
    }
  }, [])

  const registerTarget = React.useCallback<RegisterFn>(node => {
    if (node && node !== targetRef.current) {
      targetRef.current = node
      return
    }
    if (!node && targetRef.current) {
      targetRef.current = null
    }
  }, [])

  const trigger = React.useCallback(
    (options?: TriggerOptions) => {
      const source = options?.source ?? sourceRef.current
      const target = targetRef.current
      if (!source || !target) {
        return
      }
      const sourceRect = source.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const start: Point = {
        x: sourceRect.left + sourceRect.width / 2,
        y: sourceRect.top + sourceRect.height / 2,
      }
      const end: Point = {
        x: targetRect.left + targetRect.width / 2,
        y: targetRect.top + targetRect.height / 2,
      }
      const count = options?.count ?? 32
      const newCoins: CoinKeyframe[] = []
      for (let index = 0; index < count; index += 1) {
        animationId.current += 1
        newCoins.push(
          createCoinKeyframes({
            id: animationId.current,
            order: index,
            start,
            end,
          }),
        )
      }
      setCoins(prev => [...prev, ...newCoins])
    },
    [setCoins],
  )

  const removeCoin = React.useCallback((id: number) => {
    setCoins(prev => prev.filter(coin => coin.id !== id))
  }, [])

  const value = React.useMemo<CoinTransferAnimationContextValue>(
    () => ({
      registerSource,
      registerTarget,
      trigger,
    }),
    [registerSource, registerTarget, trigger],
  )

  return (
    <CoinTransferAnimationContext.Provider value={value}>
      {children}
      <Portal>
        <div className={tw('pointer-events-none fixed inset-0 z-studio-toaster overflow-visible')}>
          <AnimatePresence>
            {coins.map(coin => (
              <m.div
                key={coin.id}
                className={tw('absolute')}
                style={{ left: 0, top: 0 }}
                initial={{
                  x: coin.x[0],
                  y: coin.y[0],
                  scale: 1,
                  opacity: 0,
                  rotate: coin.rotation,
                }}
                animate={{
                  x: coin.x,
                  y: coin.y,
                  scale: [0, 1.5, 1],
                  opacity: [0, 1, 1, 0],
                  rotate: [coin.rotation, coin.rotation + 360, coin.rotation + 720],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  x: {
                    duration: coin.duration,
                    ease: 'easeInOut',
                    delay: coin.delay,
                    times: [0, 0.3, 1],
                  },
                  y: {
                    duration: coin.duration,
                    ease: 'easeInOut',
                    delay: coin.delay,
                    times: [0, 0.3, 1],
                  },
                  opacity: {
                    duration: coin.duration,
                    ease: 'linear',
                    delay: coin.delay,
                    times: [0, 0.1, 0.9, 1],
                  },
                  scale: {
                    duration: coin.duration,
                    ease: 'easeInOut',
                    times: [0, 0.3, 1],
                  },
                  rotate: {
                    duration: coin.duration,
                    ease: 'linear',
                    times: [0, 0.3, 1],
                  },
                }}
                onAnimationComplete={() => removeCoin(coin.id)}
              >
                <Coin className="h-6 w-6" />
              </m.div>
            ))}
          </AnimatePresence>
        </div>
      </Portal>
    </CoinTransferAnimationContext.Provider>
  )
}

const noopRegister: RegisterFn = () => {}
const noopTrigger: CoinTransferAnimationContextValue['trigger'] = () => {}

const EMPTY_CONTEXT: CoinTransferAnimationContextValue = {
  registerSource: noopRegister,
  registerTarget: noopRegister,
  trigger: noopTrigger,
}

export function useCoinTransferAnimation() {
  return React.useContext(CoinTransferAnimationContext) ?? EMPTY_CONTEXT
}
